import { Fragment, useMemo, useState } from "react"

// ** Third Party Components
import { DirectNormal, Notification } from "iconsax-reactjs"
import { useQuery } from "@tanstack/react-query"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Services
import { getNotificationUnreadCountService } from "@pma/services/common"

// ** Custom Components
import UserProfile from "./UserProfile"
import GlobalSearch from "./GlobalSearch"
import UpdateLogsDialog from "./UpdateLog"
import NotificationDialog from "./Notification"

const RightSection = () => {
  const { translate } = useTranslator()
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id

  const [updateLogsDialog, setUpdateLogsDialog] = useState<boolean>(false)
  const [notificationDialog, setNotificationDialog] = useState<boolean>(false)

  const { data: unreadCountData } = useQuery({
    queryKey: ["header-unread-count", orgId],
    queryFn: () =>
      getNotificationUnreadCountService({
        organization_id: orgId!,
      }),
    enabled: !!orgId,
    refetchInterval: 60000, // refresh every 60s
  })

  const unreadCount = unreadCountData?.total || 0

  return (
    <Fragment>
      {/* Desktop View */}
      <div className='hidden h-12 gap-14 rounded-full border-neutral-100 p-1 md:flex md:border'>
        <GlobalSearch />

        <div className='flex gap-2'>
          <div className='flex gap-2'>
            <div
              className='flex cursor-pointer flex-col items-center justify-between'
              onClick={() => setUpdateLogsDialog(true)}
            >
              <DirectNormal />
            </div>
            <div
              className='flex cursor-pointer flex-col items-center justify-between'
              onClick={() => setNotificationDialog(true)}
            >
              <Notification />
              {unreadCount > 0 && (
                <button className='flex gap-1 rounded bg-indigo-100 px-1 py-[2px]'>
                  <p className='text-[8px] font-semibold text-indigo-600'>({unreadCount > 9 ? '9+' : unreadCount}) {translate("New")}</p>
                </button>
              )}
            </div>
          </div>
          <div className='hidden md:block'>
            <UserProfile />
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className='flex gap-2 md:hidden'>
        <div className='flex gap-2'>
          <GlobalSearch isMobile={true} />
          <div className='flex gap-2'>
            <div className='flex gap-2'>
              <div
                className='flex cursor-pointer flex-col items-center gap-1'
                onClick={() => setUpdateLogsDialog(true)}
              >
                <DirectNormal size={20} />
              </div>
              <div
                className='flex cursor-pointer flex-col items-center gap-1'
                onClick={() => setNotificationDialog(true)}
              >
                <Notification size={20} />
                {unreadCount > 0 && (
                  <button className='rounded bg-indigo-100 px-1 py-0.5 text-[8px] font-semibold text-indigo-600'>
                    ({unreadCount > 9 ? '9+' : unreadCount}) {translate("New")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          <UserProfile />
        </div>
      </div>

      {notificationDialog && (
        <NotificationDialog
          notificationDialog={notificationDialog}
          setNotificationDialog={setNotificationDialog}
          unreadCounts={unreadCountData}
        />
      )}

      {updateLogsDialog && (
        <UpdateLogsDialog
          updateLogsDialog={updateLogsDialog}
          setUpdateLogsDialog={setUpdateLogsDialog}
        />
      )}
    </Fragment>
  )
}

export default RightSection
